/**
 * App date helpers (dayjs). Generic helpers shared with the groot shell live
 * in @groot/shell's own date.utils; this module keeps only the app-specific
 * formats the app actually uses (display format here is "DD MMM YYYY", which
 * intentionally differs from the shell's "MMM D, YYYY").
 */
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import type { Dayjs } from "dayjs";

// Initialize dayjs plugins at module load (matches @groot/shell date.utils).
// Pages call dayjs(...).fromNow() directly (e.g. Notes), so the plugins must be
// registered regardless of route load order.
dayjs.extend(relativeTime);
dayjs.extend(utc);

type DateInput = string | Date | Dayjs;

/**
 * Format date as YYYY-MM-DD
 */
export function formatDate(date?: DateInput): string {
  return dayjs(date).format("YYYY-MM-DD");
}

/**
 * Format date for display (e.g., "15 Jan 2024")
 */
export function formatDisplayDate(date?: DateInput): string {
  return dayjs(date).format("DD MMM YYYY");
}

/**
 * Format date for display with time (e.g., "15 Jan 2024, 10:30 AM")
 */
export function formatDisplayDateTime(date?: DateInput): string {
  return dayjs(date).format("DD MMM YYYY, hh:mm A");
}

/**
 * Get start of month formatted as YYYY-MM-DD
 */
export function startOfMonthFormatted(date?: DateInput): string {
  return formatDate(dayjs(date).startOf("month"));
}

/**
 * Get end of month formatted as YYYY-MM-DD
 */
export function endOfMonthFormatted(date?: DateInput): string {
  return formatDate(dayjs(date).endOf("month"));
}

/**
 * Format date with full details (e.g., "January 15, 2024 10:30:45 AM")
 * Used for detailed timestamps in detail pages
 */
export function formatDetailedDateTime(date?: DateInput): string {
  try {
    return dayjs(date).format("MMMM D, YYYY h:mm:ss A");
  } catch {
    return String(date);
  }
}

/** Current month in YYYY-MM (application-timezone naive is fine for a param). */
export function getCurrentMonthString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Shift a YYYY-MM string by ±delta months. */
export function addMonths(monthStr: string, delta: number): string {
  const [year, month] = monthStr.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  date.setMonth(date.getMonth() + delta);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
